import { useGetUserBalance, useRefreshBalance } from '@/service/payment/hook';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import { Coins, ExternalLink, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import PATH_PAGE from '@/router/path';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import Topup from '../overview/Topup';

type Props = {
  isShowPaymentLink?: boolean;
  isShowTopupButton?: boolean;
};

const ViewCredit = ({ isShowPaymentLink, isShowTopupButton }: Props) => {
  const { data, isLoading: isLoadingCredit } = useGetUserBalance();
  const { mutate: refreshBalance, isPending: isRefreshing } = useRefreshBalance();
  const totalAvailableCredit = data?.balance ?? 0;

  const handleRefresh = () => {
    refreshBalance();
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="h-4 w-4" />
            Credit Balance
          </CardTitle>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="h-7 w-7"
                  >
                    <RefreshCw
                      className={cn(
                        'h-4 w-4',
                        isRefreshing && 'animate-spin'
                      )}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Refresh balance</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isShowPaymentLink && (
              <Link
                to={PATH_PAGE.setting.payment}
                target="_blank"
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
              >
                Go to payment <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2">
          <span className="text-muted-foreground text-sm">Available Balance</span>
          <span className={`text-2xl font-semibold ${totalAvailableCredit <= 0 ? 'text-destructive' : 'text-green-600'}`}>
            {isLoadingCredit ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              fCurrencyUSDExtended(totalAvailableCredit || 0)
            )}
          </span>
        </div>
        {isShowTopupButton && (
          <div className="pt-2">
            <Topup />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ViewCredit;
